import { Observable, of } from "rxjs";
import { Account } from "./account.interface";
import { Contact } from "./contact.interface";
import { Currency } from "./currency.interface";
import { Product } from "./product.interface";
import { Country } from "./country.interface";
import { DocumentCreationInfo } from "./creation-info.interface";

export type { DocumentCreationInfo } from "./creation-info.interface";

export interface Invoice {
  id: number;
  documentSubTypeId: number;
  serialNumber: string;
  note: string;
  contactId?: number;
  contact?: Contact;
  contactName: string;
  contactPhone: string;
  driverId: number;
  salesmanId: number;
  documentDate: Date;
  createdOn?: Date;
  dueDate: Date;
  /** Offer-price (quotation) validity end date from API; distinct from invoice payment dueDate. */
  expiryDate?: Date | string;
  net?: number;
  Net?: number;
  taxAmount?: number;
  remainderAmount?: number;
  reminderAmount?: number;
  TaxAmount?: number;
  branchId: number;
  totalAmount?: number;
  TotalAmount?: number;
  branch?: Branch;
  storeId?: number;
  store?: any;
  projectId: number;
  costCenterId: number;
  currencyId: number;
  currency?: Currency;
  exchangeRate: number;
  discountAmount: number;
  discountIsPercent?: boolean;
  /** Fixed document-level discount from API (`discountMoney`). */
  discountMoney?: number;
  DiscountMoney?: number;
  /** Complementary percent when header discount was entered as a fixed amount. */
  discountPercent?: number;
  DiscountPercent?: number;
  chargeAmount: number;
  chargeIsPercent?: boolean;
  /** Complementary percent when header charge was entered as a fixed amount. */
  chargePercent?: number;
  ChargePercent?: number;
  refreanceInfo: string;
  paymentType: number | string;
  BasedOnId: number;
  pushedAmount: number;
  basedOnId: number;
  sessionId: number;
  isCommitted: boolean;
  documentPayments: any[];
  documentCharges: [];
  documentDiscounts: [];
  documentLines: DocumentLine[];
  DocumentLines?: DocumentLine[];
  journals: Journal[];
  eInvoiceState?: string;
  eInvoiceQrCode?: string | null;
  paymentDetails: PaymentDetail[];
  symbolImageUrl?:string | null;
  creationInfo?: DocumentCreationInfo;
  createdBy?: string;
  createdByUser?: {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    phoneNumber: string;
  };
  status?: string;
  goodsFlowStatus?: string;
  /** Whether the document may be edited (false when linked returns/receipts block update). */
  canEdit?: boolean;
  /** Whether a return can still be created for this document. */
  canReturn?: boolean;
  /** Optimistic concurrency token from API GET (required on purchase PUT). */
  rowVersion?: number;
}

export interface PaymentDetail {
  id: number;
  documentId: number;
  paymentType: number | string;
  accountId: number;
  glAccount: any;
  amount: number;
  description: string;
}

export interface Journal {
  id: number;
  serialNumber: string;
  journalTypeId: number;
  description: string;
  journalDate: string;
  refNumber: string;
  creditAmountTotal: number;
  debitAmountTotal: number;
  journalType?: JournalType;
  journalLines?: JournalLine[];
}

export interface JournalType {
  id: number;
  nameAr: string;
  nameEn: string;
  descriptionAr: string;
  descriptionEn: string;
}

export interface JournalLine {
  id: number;
  accountId: number;
  creditAmount: number;
  debitAmount: number;
  creditAmountLocal?: number;
  debitAmountLocal?: number;
  currencyId: number;
  exchangeRate: number;
  currencyType?: 'Local' | 'Transaction' | 'Inventory';
  amountBase: number;
  amountMethod: number;
  sourceId: number | null;
  comment: string;
  branchId: number;
  account?: Account;
  currency?: Currency;
}

export interface Branch {
  id: number;
  nameAr: string;
  nameEn: string;
  parentId: number;
  structureType: string;
  phone: string;
  additionalPhone: string;
  email: string;
  currencyId: number;
  vatRegistrationNumber: string;
  vatRegistrationName: string;
  commercialRegistrationNumber: string;
  commercialRegistrationName: string;
  logoUrl: string;
  countryCode: string;
  taxAccountId: number | null;
  regionNameOrCountrySubentity: string;
  cityName: string;
  streetName: string;
  additionalStreetName: string;
  citySubdivisionName: string;
  postalCode: string;
  buildingNumber: string;
  note: string;
  reportHeaderImage: string;
  reportFooterImage: string;
  country?: Country;
}


export enum DocumentSubType {
  Sales = 'Sales',
  SalesReturn = 'SalesReturn',
  Purches = 'Purches',
  PurchesReturn = 'PurchesReturn',
  OpeningStock = 'OpeningStock',
  OfferPrice = 'OfferPrice',
  ManualInventory = 'ManualInventory',
  POSSettlement = 'POSSettlement',
  OpeningBalance = 'OpeningBalance',
  InventorySupply = 'InventorySupply',
  InventoryDisbursement = 'InventoryDisbursement',
  PaymentVoucher = 'PaymentVoucher',
  ReceiptVoucher = 'ReceiptVoucher',
  BranchTransfer = 'BranchTransfer',
  WarehouseTransfer = 'WarehouseTransfer',
}

export enum DocumentSubTypeAR {
  Sales = 'مبيعات',
  SalesReturn = 'مرتجع مبيعات',
  Purches = 'مشتريات',
  PurchesReturn = 'مرتجع مشتريات',
  OpeningStock = 'الكمية الافتتاحية',
  OfferPrice = 'عرض سعر',
  ManualInventory = 'الجرد اليدوي',
  POSSettlement = 'تسوية نقاط البيع',
  OpeningBalance = 'الرصيد الافتتاحي',
  InventorySupply = 'أمر توريد مخزني',
  InventoryDisbursement = 'أمر صرف مخزني',
  PaymentVoucher = 'سندات الصرف',
  ReceiptVoucher = 'سندات القبض',
  BranchTransfer = 'التحويل بين الفروع',
  WarehouseTransfer = 'التحويل بين المستودعات',
}

export function getDocumentSubTypeLabels(): Observable<{ labelEn: string, labelAr: string, id: string }[]> {
  return of(Object.keys(DocumentSubType).map(key => ({
    labelEn: DocumentSubType[key as keyof typeof DocumentSubType],
    labelAr: DocumentSubTypeAR[key as keyof typeof DocumentSubType],
    id: key
  })));
}

export enum PaymentType {
  None = 0,
  Cash = 1,
  Mada = 2,
  Visa = 3,
  STCPay = 4,
  Mastercard = 5,
  BankTransfer = 6,
  DeliveryApp = 7,
  Debit = 8,
  Tamara = 9,
  Tabby = 10,
  MultiPayment = 11,
}

export enum PaymentTypeFilter {
  Cash = 'Cash',
  Visa = 'Visa',
  Debit = 'Debit',
}

/** POS API payment ids: Cash=1, Visa=3, Debit=8 (maps legacy Mada/2 → Visa/3). */
export function toPosPaymentApiId(
  type: PaymentTypeFilter | string | number | null | undefined,
): number {
  if (type === null || type === undefined || type === '') {
    return PaymentType.None;
  }
  if (
    type === PaymentTypeFilter.Cash ||
    type === PaymentType.Cash ||
    type === 1 ||
    type === '1' ||
    type === 'Cash'
  ) {
    return PaymentType.Cash;
  }
  if (
    type === PaymentTypeFilter.Visa ||
    type === PaymentType.Visa ||
    type === 3 ||
    type === '3' ||
    type === 'Visa' ||
    type === PaymentType.Mada ||
    type === 2 ||
    type === '2' ||
    type === 'Mada'
  ) {
    return PaymentType.Visa;
  }
  if (
    type === PaymentTypeFilter.Debit ||
    type === PaymentType.Debit ||
    type === 8 ||
    type === '8' ||
    type === 'Debit'
  ) {
    return PaymentType.Debit;
  }
  const numeric = Number(type);
  if (numeric === 2) {
    return PaymentType.Visa;
  }
  if (
    numeric === PaymentType.Cash ||
    numeric === PaymentType.Visa ||
    numeric === PaymentType.Debit
  ) {
    return numeric;
  }
  return PaymentType.None;
}

export function resolvePosPaymentFilter(
  type: PaymentTypeFilter | string | number | null | undefined,
): PaymentTypeFilter | null {
  const apiId = toPosPaymentApiId(type);
  if (apiId === PaymentType.Cash) {
    return PaymentTypeFilter.Cash;
  }
  if (apiId === PaymentType.Visa) {
    return PaymentTypeFilter.Visa;
  }
  if (apiId === PaymentType.Debit) {
    return PaymentTypeFilter.Debit;
  }
  return null;
}

export const PaymentTypeTranslations: Record<PaymentType, string> = {
  [PaymentType.None]: 'غير محدد',
  [PaymentType.Cash]: 'نقدي',
  [PaymentType.Mada]: 'مدى',
  [PaymentType.Visa]: 'فيزا',
  [PaymentType.STCPay]: 'STC Pay',
  [PaymentType.Mastercard]: 'ماستركارد',
  [PaymentType.BankTransfer]: 'تحويل بنكي',
  [PaymentType.DeliveryApp]: 'تطبيق توصيل',
  [PaymentType.Debit]: 'مدين',
  [PaymentType.Tamara]: 'تمارا',
  [PaymentType.Tabby]: 'تابي',
  [PaymentType.MultiPayment]: 'دفع متعدد',
};

export const PaymentMethodsList = [
  { id: 1, nameEn: 'Cash', nameAr: 'نقدي' },
  { id: 2, nameEn: 'Mada', nameAr: 'مدى' },
  { id: 3, nameEn: 'Visa', nameAr: 'فيزا' },
  { id: 4, nameEn: 'STC Pay', nameAr: 'STC Pay' },
  { id: 5, nameEn: 'Mastercard', nameAr: 'ماستركارد' },
  { id: 6, nameEn: 'Bank Transfer', nameAr: 'تحويل بنكي' },
  { id: 7, nameEn: 'Delivery App', nameAr: 'تطبيق توصيل' },
  { id: 8, nameEn: 'Debit', nameAr: 'مدين' },
  { id: 9, nameEn: 'Tamara', nameAr: 'تمارا' },
  { id: 10, nameEn: 'Tabby', nameAr: 'تابي' },
  { id: 11, nameEn: 'MultiPayment', nameAr: 'دفع متعدد' },
];


export interface CreateDocument {
  DocumentSubTypeId?: DocumentSubType;
  Note?: string;
  ContactId?: string;
  ContactName?: string;
  ContactPhone?: string;
  DriverId?: string;
  SalesmanId?: string;
  DocumentDate: string;
  DueDate?: string;
  BranchId?: string;
  ProjectId?: string;
  CostCenterId?: string;
  CurrencyId?: string;
  ExchangeRate?: number;
  DiscountAmount?: number;
  DiscountIsPercent?: boolean;
  ChargeAmount?: number;
  ChargeIsPercent?: boolean;
  RefreanceInfo?: string;
  PushedAmount?: number;
  BasedOnId?: string;
  SessionId?: number;
  IsCommitted?: boolean;
  DocumentPayments: DocumentPayment[];
  DocumentCharges: DocumentCharge[];
  DocumentDiscounts: DocumentDiscount[];
  DocumentLines: DocumentLine[];
}

export interface DocumentPayment {
  PaymentType: PaymentType;
  AccountId: string;
  Amount: number;
  Comment?: string;
}

export interface DocumentCharge {
  JournalId?: string;
  JournalLineId?: string;
  AccountId?: string;
  Amount?: number;
  Comment?: string;
}

export interface DocumentDiscount {
  AccountId: string;
  Amount: number;
  Comment?: string;
}

export interface DocumentLine {
  id?: number;
  ProductId?: string;
  product?: Product;
  /** Full product graph when API sends it separately from `product` (e.g. units with withTax). */
  completeProduct?: Product;
  productName?: string;
  ProductDescription?: string;
  UnitId?: string;
  unitId?: string;
  unit?: string | number;
  UnitSize?: number;
  Quantity: number;
  quantity?: number;
  availableQuantity?: number;
  CostAmount?: number;
  Price?: number;
  productId: number;
  price?: number;
  DiscountAmount?: number;
  DiscountIsPercent?: boolean;
  ChargeAmount?: number;
  ChargeIsPercent?: boolean;
  TaxAmount?: number;
  taxAmount?: number;
  discountEnteredAtLineLevel?: boolean;
  chargeEnteredAtLineLevel?: boolean;
  lineDiscountBeforeDistributionAmount?: number;
  lineDiscountBeforeDistributionIsPercent?: boolean;
  lineChargeBeforeDistributionAmount?: number;
  lineChargeBeforeDistributionIsPercent?: boolean;
  distributedDiscountExtendedAmount?: number;
  distributedChargeExtendedAmount?: number;
  /**
   * Line grand total (incl. tax) from GET document — use for immutable historical display.
   * Backend may send PascalCase `Net`.
   */
  net?: number;
  Net?: number;
  ExciseTaxAmount?: number;
  ExpirationDate?: string;
  StoreId?: string;
  ProjectId?: string;
  CostCenterId?: string;
  basedOnLineId?: number;
  purchaseDocumentLineId?: number;
  salesDocumentLineId?: number;
  SessionId?: number;
  DocumentLineCharges?: DocumentLineCharge[];
  DocumentLineDiscounts?: DocumentLineDiscount[];
  /** False when backend locked the line due to subsequent inventory movements. */
  canEdit?: boolean;
}

export interface DocumentLineCharge {
  JournalId?: string;
  JournalLineId?: string;
  AccountId?: string;
  Amount?: number;
  Comment?: string;
}

export interface DocumentLineDiscount {
  AccountId: string;
  Amount: number;
  Comment?: string;
}

export enum QuotationStatus {
  None = 'None',
  Draft = 'Draft',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Cancelled = 'Cancelled',
  Expired = 'Expired',
  Converted = 'Converted',
  InExecution = 'InExecution',
  Executed = 'Executed',
  Sent = 'Sent'
}


